const DEFAULT_TEXT_TRANSFORM = {
  transformType: 'TEXT',
  textCase: 'CAPITALISED',
  spaceReplacement: ' ',
};

const DEFAULT_DATE_TRANSFORM = {
  transformType: 'DATE',
  dateStyle: 'DD_MMM_YYYY',
};

export const TEXT_CASE_OPTIONS = ['UPPER', 'LOWER', 'CAPITALISED', 'NONE'];

export const TEXT_CASE_LABELS = {
  UPPER: 'Uppercase',
  LOWER: 'Lowercase',
  CAPITALISED: 'Capitalised',
  NONE: 'No case change',
};

export const DATE_STYLE_OPTIONS = [
  'DD_MM_YYYY',
  'YYYY_MM_DD',
  'MMM_DD_YYYY',
  'DD_MMM_YYYY',
  'YYYY_MM_DD_DASH',
  'COMPACT',
];

export const DATE_STYLE_LABELS = {
  DD_MM_YYYY: 'dd/MM/yyyy',
  YYYY_MM_DD: 'yyyy/MM/dd',
  MMM_DD_YYYY: 'MMM dd yyyy',
  DD_MMM_YYYY: 'dd MMM yyyy',
  YYYY_MM_DD_DASH: 'yyyy-MM-dd',
  COMPACT: 'yyyyMMdd',
};

export const TOKEN_METADATA = {
  SENDER_ORGANISATION: {
    label: 'Sender Organisation',
    transformKind: 'TEXT',
    rawExample: 'St Marys Hospital',
  },
  SENDER_LOCATION: {
    label: 'Sender Location',
    transformKind: 'TEXT',
    rawExample: 'London',
  },
  SPECIALTY: {
    label: 'Specialty',
    transformKind: 'TEXT',
    rawExample: 'Cardiology',
  },
  SENDER_SURNAME: {
    label: 'Sender Surname',
    transformKind: 'TEXT',
    rawExample: 'Smith',
  },
  SENDER_NAME: {
    label: 'Sender Name',
    transformKind: 'TEXT',
    rawExample: 'John Smith',
  },
  SENDER_EMAIL: {
    label: 'Sender Email',
    transformKind: 'TEXT',
    rawExample: 'doctor@clinic.com',
  },
  SENDER_PHONE: {
    label: 'Sender Phone',
    transformKind: 'NONE',
    rawExample: '02012345678',
  },
  RECIPIENT_NAME: {
    label: 'Recipient Name',
    transformKind: 'TEXT',
    rawExample: 'Jane Doe',
  },
  RECIPIENT_SURNAME: {
    label: 'Recipient Surname',
    transformKind: 'TEXT',
    rawExample: 'Doe',
  },
  RECIPIENT_ORGANISATION: {
    label: 'Recipient Organisation',
    transformKind: 'TEXT',
    rawExample: 'NHS Trust',
  },
  RECIPIENT_LOCATION: {
    label: 'Recipient Location',
    transformKind: 'TEXT',
    rawExample: 'Manchester',
  },
  RECIPIENT_SPECIALTY: {
    label: 'Recipient Specialty',
    transformKind: 'TEXT',
    rawExample: 'General Practice',
  },
  LETTER_TYPE: {
    label: 'Letter Type',
    transformKind: 'TEXT',
    rawExample: 'Referral',
  },
  CLINICAL_DEPARTMENT: {
    label: 'Clinical Department',
    transformKind: 'TEXT',
    rawExample: 'Healthcare services',
  },
  DATE: {
    label: 'Date',
    transformKind: 'DATE',
    rawExample: '2026-01-10',
  },
  PAGE_COUNT: {
    label: 'Page Count',
    transformKind: 'NONE',
    rawExample: '3PGS',
  },
  ASSIGNEE_NAME: {
    label: 'Assignee Name',
    transformKind: 'TEXT',
    rawExample: 'James Watson',
  },
};

export const getTransformKind = (token) => TOKEN_METADATA[token]?.transformKind ?? 'TEXT';

export const getDefaultTransform = (token) => {
  const kind = getTransformKind(token);
  if (kind === 'NONE') {
    return null;
  }
  if (kind === 'DATE') {
    return { ...DEFAULT_DATE_TRANSFORM };
  }
  return { ...DEFAULT_TEXT_TRANSFORM };
};

const normalizeTextTransform = (transform) => {
  if (!transform || transform.transformType !== 'TEXT') {
    return { ...DEFAULT_TEXT_TRANSFORM };
  }

  const textCase = TEXT_CASE_OPTIONS.includes(transform.textCase)
    ? transform.textCase
    : DEFAULT_TEXT_TRANSFORM.textCase;
  const spaceReplacement =
    typeof transform.spaceReplacement === 'string' && transform.spaceReplacement.length > 0
      ? transform.spaceReplacement
      : DEFAULT_TEXT_TRANSFORM.spaceReplacement;

  return { transformType: 'TEXT', textCase, spaceReplacement };
};

const normalizeDateTransform = (transform) => {
  if (!transform || transform.transformType !== 'DATE') {
    return { ...DEFAULT_DATE_TRANSFORM };
  }

  const dateStyle = DATE_STYLE_OPTIONS.includes(transform.dateStyle)
    ? transform.dateStyle
    : DEFAULT_DATE_TRANSFORM.dateStyle;

  return { transformType: 'DATE', dateStyle };
};

export const isValidTextTransform = (transform) =>
  transform?.transformType === 'TEXT' &&
  TEXT_CASE_OPTIONS.includes(transform.textCase) &&
  typeof transform.spaceReplacement === 'string' &&
  transform.spaceReplacement.length > 0;

export const isValidDateTransform = (transform) =>
  transform?.transformType === 'DATE' && DATE_STYLE_OPTIONS.includes(transform.dateStyle);

export const getTextTransformSpaceReplacementError = (transform) => {
  if (!transform || transform.transformType !== 'TEXT') {
    return null;
  }
  if (typeof transform.spaceReplacement !== 'string' || transform.spaceReplacement.length === 0) {
    return 'Space replacement cannot be empty';
  }
  return null;
};

export const getTitleComponentValidationError = (component) => {
  if (component.type !== 'TOKEN') {
    return null;
  }

  const transformKind = getTransformKind(component.token);
  if (transformKind === 'NONE') {
    return null;
  }

  const transform = component.transform ?? getDefaultTransform(component.token);
  if (transformKind === 'TEXT') {
    if (!TEXT_CASE_OPTIONS.includes(transform?.textCase)) {
      return 'Choose a case option';
    }
    return getTextTransformSpaceReplacementError(transform);
  }
  if (transformKind === 'DATE' && !isValidDateTransform(transform)) {
    return 'Choose a date format';
  }

  return null;
};

export const validateTitleComponents = (components) =>
  (components || [])
    .map((component, index) => ({ index, error: getTitleComponentValidationError(component) }))
    .filter(({ error }) => error);

const capitaliseWord = (word) => {
  if (!word) {
    return word;
  }
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export const applyTextTransform = (raw, { textCase, spaceReplacement }) => {
  if (raw === '--') {
    return raw;
  }

  let result = raw.trim();

  switch (textCase) {
    case 'UPPER':
      result = result.toUpperCase();
      break;
    case 'LOWER':
      result = result.toLowerCase();
      break;
    case 'CAPITALISED':
      result = result
        .split(/\s+/)
        .filter(Boolean)
        .map(capitaliseWord)
        .join(spaceReplacement);
      break;
    case 'NONE':
    default:
      break;
  }

  return result.replace(/ /g, spaceReplacement);
};

const parsePreviewDate = (raw) => {
  const trimmed = raw.trim();
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
  }
  const compactMatch = trimmed.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compactMatch) {
    return new Date(Number(compactMatch[1]), Number(compactMatch[2]) - 1, Number(compactMatch[3]));
  }
  return new Date(trimmed);
};

const pad2 = (value) => String(value).padStart(2, '0');

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const applyDateTransform = (raw, dateStyle) => {
  if (raw === '--') {
    return raw;
  }

  const date = parsePreviewDate(raw);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }

  const day = pad2(date.getDate());
  const month = pad2(date.getMonth() + 1);
  const year = date.getFullYear();
  const monthShort = MONTHS_SHORT[date.getMonth()];

  switch (dateStyle) {
    case 'DD_MM_YYYY':
      return `${day}/${month}/${year}`;
    case 'YYYY_MM_DD':
      return `${year}/${month}/${day}`;
    case 'MMM_DD_YYYY':
      return `${monthShort} ${day} ${year}`;
    case 'DD_MMM_YYYY':
      return `${day} ${monthShort} ${year}`;
    case 'YYYY_MM_DD_DASH':
      return `${year}-${month}-${day}`;
    case 'COMPACT':
      return `${year}${month}${day}`;
    default:
      return raw;
  }
};

export const previewTokenComponent = (component) => {
  const meta = TOKEN_METADATA[component.token];
  if (!meta) {
    return '--';
  }

  if (getTransformKind(component.token) === 'NONE') {
    return meta.rawExample;
  }

  const transform = component.transform ?? getDefaultTransform(component.token);
  if (!transform) {
    return meta.rawExample;
  }

  if (transform.transformType === 'TEXT') {
    return applyTextTransform(meta.rawExample, transform);
  }
  if (transform.transformType === 'DATE') {
    return applyDateTransform(meta.rawExample, transform.dateStyle);
  }

  return meta.rawExample;
};

export const formatTransformSummary = (transform, token) => {
  if (!transform) {
    return '';
  }

  if (transform.transformType === 'TEXT') {
    const space = transform.spaceReplacement === ' ' ? 'space' : transform.spaceReplacement;
    return `${transform.textCase}, ${space}`;
  }
  if (transform.transformType === 'DATE') {
    return DATE_STYLE_LABELS[transform.dateStyle] || transform.dateStyle;
  }

  return getTransformKind(token);
};

export const normalizeTitleComponent = (component) => {
  if (component.type !== 'TOKEN') {
    return { ...component, token: null, transform: null };
  }

  const transformKind = getTransformKind(component.token);
  if (transformKind === 'NONE') {
    return { type: 'TOKEN', token: component.token, value: null, transform: null };
  }

  const rawTransform = component.transform ?? getDefaultTransform(component.token);
  const transform =
    transformKind === 'TEXT'
      ? normalizeTextTransform(rawTransform)
      : transformKind === 'DATE'
        ? normalizeDateTransform(rawTransform)
        : rawTransform;

  return {
    type: 'TOKEN',
    token: component.token,
    value: null,
    transform,
  };
};

export const normalizeTitleComponents = (components) =>
  (components || []).map(normalizeTitleComponent);

export const prepareTitleComponentsForSave = (components) =>
  components.map((component) => {
    if (component.type === 'LITERAL') {
      return {
        type: 'LITERAL',
        token: null,
        value: component.value,
        transform: null,
      };
    }

    const transformKind = getTransformKind(component.token);
    return {
      type: 'TOKEN',
      token: component.token,
      value: null,
      ...(transformKind !== 'NONE' && component.transform ? { transform: component.transform } : {}),
    };
  });

export const generateTitlePreview = (components) =>
  components
    .map((component) =>
      component.type === 'TOKEN' ? previewTokenComponent(component) : component.value || ''
    )
    .join('') + '.pdf';

export const getPalettePreview = (tokenKey) => {
  const component = {
    type: 'TOKEN',
    token: tokenKey,
    transform: getDefaultTransform(tokenKey),
  };
  return previewTokenComponent(component);
};
